import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { uploadService } from '../services/api';
import ConnectorsTab from '../components/connectors/ConnectorsTab';
import MintsoftConnectorsTab from '../components/connectors/MintsoftConnectorsTab';
import { useNotification } from '../contexts/NotificationContext';
// Removed logo imports - using clean icon-based design instead
import {
  FolderIcon,
  UploadIcon,
  Trash2Icon,
  FileTextIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
  DatabaseIcon,
  ServerIcon,
  SearchIcon,
  XCircleIcon,
  CalendarIcon,
  PlayIcon,
  CheckIcon,
  PlusCircleIcon,
  FolderOpenIcon,
  DownloadIcon
} from 'lucide-react';

function DataSources() {
  const [uploads, setUploads] = useState([]);
  const [datasets, setDatasets] = useState({});
  const [selectedDatasetForVersions, setSelectedDatasetForVersions] = useState(null);
  const [datasetVersions, setDatasetVersions] = useState([]);
  
  // Upload versioning states
  const [showVersioningForm, setShowVersioningForm] = useState(false);
  const [uploadDatasetName, setUploadDatasetName] = useState('');
  const [uploadParentDatasetId, setUploadParentDatasetId] = useState('');
  const [uploadVersionDescription, setUploadVersionDescription] = useState('');
  const [createNewDataset, setCreateNewDataset] = useState(true);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileValidationResults, setFileValidationResults] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'files', 'connectors', or 'custom-connectors'
  const fileInputRef = useRef();
  const { notify } = useNotification();

  // Fetch data on component mount and when tab changes
  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
    if (activeTab === 'files' || activeTab === 'overview') {
      fetchUploads();
    }
  }, [activeTab]);
  
  // Handle versioning form visibility based on selected files
  useEffect(() => {
    if (selectedFiles.length > 1) {
      // Multiple files selected, hide versioning form
      setShowVersioningForm(false);
    } else if (selectedFiles.length === 1) {
      // Single file selected, show versioning form by default
      setShowVersioningForm(true);
    }
  }, [selectedFiles]);

  // Fetch uploads from API with retry mechanism
  const fetchUploads = async (retries = 3, delay = 1000) => {
    let attempts = 0;
    
    const attemptFetch = async () => {
      try {
        setLoading(true);
        const data = await uploadService.getUploads();
        setUploads(data || []);

        // Also fetch datasets for versioning
        const datasetData = await uploadService.getDatasets();
        setDatasets(datasetData?.datasets || datasetData || {});
        
        return true;
      } catch (error) {
        console.error(`Error fetching uploads (attempt ${attempts+1}/${retries}):`, error);
        if (attempts >= retries - 1) {
          notify.error('Failed to load data sources');
          return false;
        }
        return false;
      } finally {
        setLoading(false);
      }
    };
    
    // First attempt
    attempts++;
    if (await attemptFetch()) return;
    
    // Retry with delay
    for (let i = 1; i < retries; i++) {
      attempts++;
      await new Promise(r => setTimeout(r, delay));
      if (await attemptFetch()) return;
    }
  };

  // Fetch database connections

  // Handle file selection via button
  const handleFileSelect = (e) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
      setFileValidationResults({}); // Clear previous validation results
    }
  };

  // Handle file drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(Array.from(e.dataTransfer.files));
      setFileValidationResults({}); // Clear previous validation results
    }
  };

  // Upload selected files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      notify.warning('Please select files to upload');
      return;
    }

    // Validate file formats and sizes
    const CSV_MAX_SIZE_MB = 100;
    const CSV_MAX_SIZE_BYTES = CSV_MAX_SIZE_MB * 1024 * 1024;

    for (const file of selectedFiles) {
      const fileExtension = file.name.split('.').pop().toLowerCase();

      // Block CSV files larger than 100MB
      if (fileExtension === 'csv' && file.size > CSV_MAX_SIZE_BYTES) {
        notify.error(
          `CSV files larger than ${CSV_MAX_SIZE_MB}MB are not supported. ` +
          `"${file.name}" is ${formatFileSize(file.size)}. ` +
          `Please convert to Parquet format for better performance and smaller file sizes.`,
          { duration: 15000 }
        );
        return;
      }
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Determine if we should use the large file upload endpoint
      const hasLargeFiles = selectedFiles.some(file => file.size > 10 * 1024 * 1024); // 10MB
      
      // Prepare versioning options if versioning form is shown
      // Simulate progress (actual upload doesn't have progress tracking)
      let progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + Math.random() * 10;
          return newProgress >= 90 ? 90 : newProgress;
        });
      }, 500);
      
      // Upload the files
      const uploadMethod = hasLargeFiles ? uploadService.uploadLargeFiles : uploadService.uploadFiles;
      
      // Debug - print FormData contents
      if (!hasLargeFiles) {
        const formData = new FormData();
        for (let i = 0; i < selectedFiles.length; i++) {
          formData.append('files', selectedFiles[i]);
        }
        console.log('FormData entries:');
        for (let pair of formData.entries()) {
          console.log(pair[0], pair[1]);
        }
      }
      
      // Prepare versioning options
      let versioningOptions = {};
      if (createNewDataset) {
        versioningOptions.dataset_name = uploadDatasetName || `dataset_${Date.now()}`;
      } else {
        // Find the dataset name from the selected parent
        const parentDatasets = Object.entries(datasets).find(([name, versions]) =>
          versions.some(v => v.upload_id === uploadParentDatasetId)
        );
        if (parentDatasets) {
          versioningOptions.dataset_name = parentDatasets[0];
          versioningOptions.parent_dataset_id = uploadParentDatasetId;
        }
      }

      // Use parallel uploads for multiple files if not large files
      let uploadResult;
      const validationResults = {}; // Initialize here to prevent errors
      if (uploadVersionDescription) {
        versioningOptions.version_description = uploadVersionDescription;
      }
      
      if (selectedFiles.length > 1 && !hasLargeFiles) {
        // Track upload progress for each file
        const fileUploadProgress = {};
        selectedFiles.forEach(file => {
          fileUploadProgress[file.name] = { progress: 0, completed: false };
        });
        
        // Update progress display
        progressInterval = setInterval(() => {
          // Calculate overall progress based on individual file progress
          const totalProgress = Object.values(fileUploadProgress)
            .reduce((sum, file) => sum + file.progress, 0) / selectedFiles.length;
          
          setUploadProgress(Math.min(90, totalProgress));
        }, 500);
        
        // Process each file in parallel
        const uploadPromises = [];
        const uploadResults = [];
        
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          
          // Create a custom versioning option for each file
          const fileOptions = {...versioningOptions};
          if (fileOptions.dataset_name && selectedFiles.length > 1) {
            // Add file-specific suffix to dataset name if uploading multiple files
            fileOptions.dataset_name = `${fileOptions.dataset_name}_${i + 1}`;
          }
          
          // Upload single file and track its progress
          const uploadPromise = uploadService.uploadFiles([file], fileOptions)
            .then(result => {
              // Mark as completed
              fileUploadProgress[file.name] = { progress: 100, completed: true };
              uploadResults.push(result);
              return result;
            })
            .catch(error => {
              // Mark as completed even on error
              fileUploadProgress[file.name] = { progress: 100, completed: true };
              throw error;
            });
          
          uploadPromises.push(uploadPromise);
        }
        
        // Wait for all uploads to complete
        await Promise.all(uploadPromises)
          .catch(error => {
            clearInterval(progressInterval);
            notify.error(`Error during parallel upload: ${error.message}`);
            throw error;
          });
        
        // Combine results
        uploadResult = {
          status: 'complete',
          message: `Successfully uploaded ${uploadResults.length} file(s)`,
          files: uploadResults.flatMap(r => r.files || []),
          validation_data: uploadResults.flatMap(r => r.validation_data || [])
        };
        
        clearInterval(progressInterval);
        setUploadProgress(100);
      } else {
        // Use original single-file or large file upload method
        // Simulate progress (actual upload doesn't have progress tracking)
        progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 5, 90));
        }, 500);
        
        // Choose upload method based on file size
        const uploadMethod = hasLargeFiles ? uploadService.uploadLargeFiles : uploadService.uploadFiles;
        
        // Upload files
        uploadResult = await uploadMethod(selectedFiles, versioningOptions);
      }
      
      // Process validation results
      if (uploadResult && uploadResult.validation_data) {
        for (const fileResult of uploadResult.validation_data) {
          const { filename, validation_warnings, file_validation } = fileResult;
          
          // Store validation results
          validationResults[filename] = {
            preUploadWarnings: validation_warnings || [],
            postUploadValidation: file_validation
          };
          
          // Display warnings and errors
          if (validation_warnings && validation_warnings.length > 0) {
            validation_warnings.forEach(warning => {
              notify.warning(`${filename}: ${warning}`, { duration: 10000 });
            });
          }
          
          if (file_validation) {
            if (file_validation.warnings && file_validation.warnings.length > 0) {
              file_validation.warnings.forEach(warning => {
                notify.warning(`${filename}: ${warning}`, { duration: 10000 });
              });
            }
            
            if (file_validation.errors && file_validation.errors.length > 0) {
              file_validation.errors.forEach(error => {
                notify.error(`${filename}: ${error}`, { duration: 15000 });
              });
            }
            
            // Show comma analysis details if available
            if (file_validation.comma_analysis && file_validation.comma_analysis.has_comma_issues) {
              const analysis = file_validation.comma_analysis;
              if (analysis.suggested_delimiter) {
                notify.info(
                  `${filename}: Detected '${analysis.suggested_delimiter}' as delimiter instead of comma`,
                  { duration: 12000 }
                );
              }
            }
          }
        }
      }
      
      // Update validation results state
      setFileValidationResults(validationResults);
      
      // Show overall upload success message
      const hasAnyWarnings = uploadResult?.validation_data?.some(file => 
        (file.validation_warnings && file.validation_warnings.length > 0) ||
        (file.file_validation?.warnings && file.file_validation.warnings.length > 0)
      );
      
      const hasAnyErrors = uploadResult?.validation_data?.some(file => 
        (file.file_validation?.errors && file.file_validation.errors.length > 0)
      );
      
      if (hasAnyErrors) {
        notify.error(`Upload completed with ${selectedFiles.length} file(s), but some files have validation errors.`);
      } else if (hasAnyWarnings) {
        notify.success(`Upload completed successfully with ${selectedFiles.length} file(s). Some files have validation warnings - please review.`);
      } else {
        notify.success(`Upload completed successfully with ${selectedFiles.length} file(s). All files passed validation.`);
      }
      
      // Complete progress and clear interval
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Clear selected files and refresh uploads list
      setSelectedFiles([]);
      
      // Clear validation results after a delay to show success/warning notifications first
      setTimeout(() => {
        setFileValidationResults({});
      }, 3000);
      
      // Reset versioning form
      setShowVersioningForm(false);
      setUploadDatasetName('');
      setUploadParentDatasetId('');
      setUploadVersionDescription('');
      setCreateNewDataset(true);
      
      // Add a delay before fetching uploads to ensure server has processed the upload
      
      // Wait a moment before refreshing to let the server process the upload
      setTimeout(() => {
        fetchUploads();
      }, 1500);
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        console.error('Error headers:', JSON.stringify(error.response.headers));
        
        // Special handling for S3 errors
        if (error.response.data && typeof error.response.data === 'string' && 
            error.response.data.includes('<Code>SignatureDoesNotMatch</Code>')) {
          console.error('S3 Signature error detected. This is likely due to header mismatches when uploading to S3.');
          notify.error('S3 signature error: Upload failed due to signature mismatch. Please try again or contact support if this persists.');
          return;
        }
      }
      
      // Provide clear error message with specific details
      if (error.message && error.message.includes('Authentication required')) {
        notify.error('Please log in again to upload files');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        notify.error('Authentication failed. Please refresh the page and log in again.');
      } else if (error.message && error.message.includes('Network Error')) {
        console.error('Network error detected - possible CORS issue or connectivity problem');
        notify.error('Network error: Cannot connect to upload server. Please try again.');
      } else {
        // General error handling with more information
        const errorDetail = error.response?.data?.detail || error.message;
        console.error('Upload error details:', errorDetail);
        notify.error(`Failed to upload files: ${errorDetail}`);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete an upload
  const handleDeleteUpload = async (uploadId) => {
    try {
      await uploadService.deleteUpload(uploadId);

      // Remove from state
      setUploads(prev => prev.filter(upload => upload.upload_id !== uploadId));
      setShowDeleteConfirm(null);
      notify.success('Data source deleted successfully');

    } catch (error) {
      console.error('Delete error:', error);

      // Check if it's a CORS error - these actually succeed on the backend
      if (error.message && error.message.includes('CORS') ||
          error.message && error.message.includes('Network Error') ||
          (error.response === undefined && error.request)) {
        // CORS error but request likely succeeded - treat as success
        setUploads(prev => prev.filter(upload => upload.upload_id !== uploadId));
        setShowDeleteConfirm(null);
        notify.success('Data source deleted successfully');
      } else {
        notify.error('Failed to delete data source');
      }
    }
  };

  // Download a dataset
  const handleDownloadDataset = async (uploadId, e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      notify.info('Preparing download...');
      await uploadService.downloadDataset(uploadId);
      notify.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      notify.error('Failed to download dataset');
    }
  };


  // Handle viewing versions for a dataset
  const handleViewVersions = async (datasetName) => {
    try {
      setSelectedDatasetForVersions(datasetName);
      const versions = datasets[datasetName] || [];
      setDatasetVersions(versions);
    } catch (error) {
      console.error(`Error loading versions for dataset ${datasetName}:`, error);
      notify.error('Failed to load dataset versions');
    }
  };

  // Clear selected files
  const handleClearSelectedFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Reset versioning form
    setShowVersioningForm(false);
    setUploadDatasetName('');
    setUploadParentDatasetId('');
    setUploadVersionDescription('');
    setCreateNewDataset(true);
  };


  // Format file size for display
  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else if (sizeInBytes < 1024 * 1024 * 1024) {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  // Format date to locale string
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr || 'Unknown';
    }
  };

  // Render the drag and drop upload area
  const renderUploadArea = () => (
    <div className="p-6 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 mb-8">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Upload Files</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        CSV and Parquet supported
      </p>
      
      {/* Drag and drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-12 transition-colors ${
          dragging 
            ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/10 dark:border-primary-400' 
            : 'border-slate-300 hover:border-slate-400 dark:border-slate-600 dark:hover:border-slate-500'
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <UploadIcon className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500 mb-4" />
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
            Drop files here or{' '}
            <button
              type="button"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transform hover:scale-110 transition-all duration-200"
              onClick={() => fileInputRef.current?.click()}
            >
              browse
            </button>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            CSV: max 100MB • Parquet: max 1GB
          </p>
        </div>
      </div>
      
      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <div className="mt-4">
          <div className="space-y-2">
            {selectedFiles.map((file, index) => {
              const validationResult = fileValidationResults[file.name];
              const hasWarnings = (validationResult?.preUploadWarnings?.length > 0) || 
                                (validationResult?.postUploadValidation?.warnings?.length > 0);
              const hasErrors = validationResult?.postUploadValidation?.errors?.length > 0;
              
              return (
                <div key={index} className="flex items-center justify-between py-2 px-3 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                  <div className="flex items-center flex-1 min-w-0">
                    <FileTextIcon className="h-4 w-4 text-slate-400 dark:text-slate-500 mr-3 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-200 truncate">{file.name}</p>
                        {hasErrors && (
                          <XCircleIcon className="h-4 w-4 text-red-500 ml-2 flex-shrink-0" title="Validation errors" />
                        )}
                        {!hasErrors && hasWarnings && (
                          <AlertTriangleIcon className="h-4 w-4 text-yellow-500 ml-2 flex-shrink-0" title="Validation warnings" />
                        )}
                        {validationResult && !hasErrors && !hasWarnings && (
                          <CheckIcon className="h-4 w-4 text-green-500 ml-2 flex-shrink-0" title="Validation passed" />
                        )}
                      </div>
                      <div className="flex items-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(file.size)}</p>
                        {validationResult?.postUploadValidation?.format && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {validationResult.postUploadValidation.format.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Show validation details on hover or click */}
                  {validationResult && (hasWarnings || hasErrors) && (
                    <div className="ml-2 flex-shrink-0">
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        title="Click to see validation details"
                        onClick={() => {
                          const details = [];
                          if (validationResult.preUploadWarnings?.length > 0) {
                            details.push(...validationResult.preUploadWarnings);
                          }
                          if (validationResult.postUploadValidation?.warnings?.length > 0) {
                            details.push(...validationResult.postUploadValidation.warnings);
                          }
                          if (validationResult.postUploadValidation?.errors?.length > 0) {
                            details.push(...validationResult.postUploadValidation.errors);
                          }
                          notify.info(`${file.name} validation details:\n${details.join('\n')}`, { duration: 8000 });
                        }}
                      >
                        Details
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Dataset Versioning Form - only shown for single file uploads */}
          {selectedFiles.length === 1 && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Dataset Versioning
                </label>
                <button
                  type="button"
                  onClick={() => setShowVersioningForm(!showVersioningForm)}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {showVersioningForm ? 'Hide' : 'Show'} Options
                </button>
              </div>
            
              {showVersioningForm && (
                <div className="space-y-3">
                  {/* New Dataset vs Version */}
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="datasetType"
                        value="new"
                        checked={createNewDataset}
                        onChange={() => setCreateNewDataset(true)}
                        className="mr-2"
                      />
                      <span className="text-sm">New Dataset</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="datasetType"
                        value="version"
                        checked={!createNewDataset}
                        onChange={() => setCreateNewDataset(false)}
                        className="mr-2"
                      />
                      <span className="text-sm">New Version</span>
                    </label>
                  </div>
                  
                  {createNewDataset ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Dataset Name
                      </label>
                      <input
                        type="text"
                        value={uploadDatasetName}
                        onChange={(e) => setUploadDatasetName(e.target.value)}
                        placeholder="Enter dataset name"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-slate-700 dark:text-white"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parent Dataset
                      </label>
                      <select
                        value={uploadParentDatasetId}
                        onChange={(e) => setUploadParentDatasetId(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-slate-700 dark:text-white"
                      >
                        <option value="">Select parent dataset</option>
                        {Object.entries(datasets).map(([datasetName, versions]) => (
                          <optgroup key={datasetName} label={datasetName}>
                            {versions.map((version) => (
                              <option key={version.upload_id} value={version.upload_id}>
                                {version.version_description || `Version ${new Date(version.created_at).toLocaleDateString()}`}
                                {version.is_latest && ' (Latest)'}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Version Description
                    </label>
                    <input
                      type="text"
                      value={uploadVersionDescription}
                      onChange={(e) => setUploadVersionDescription(e.target.value)}
                      placeholder="Describe what's new in this version"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-slate-700 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200"
              onClick={handleClearSelectedFiles}
              disabled={isUploading}
            >
              Clear
            </button>
            <button
              type="button"
              className={`px-4 py-1.5 text-sm text-white rounded-lg transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200 ${
                isUploading 
                  ? 'bg-primary-400 cursor-not-allowed' 
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? (
                <div className="flex items-center">
                  <RefreshCwIcon className="animate-spin mr-2 h-4 w-4" />
                  Uploading
                </div>
              ) : (
                'Upload'
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Upload progress bar */}
      {isUploading && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-1">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Uploading...</p>
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">{Math.round(uploadProgress)}%</p>
          </div>
          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
            <div 
              className="bg-primary-600 dark:bg-primary-500 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );

  // Render empty state for data sources
  const renderEmptyDataSources = () => (
    <div className="text-center py-12">
      <FolderIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-600" />
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No datasets found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
        Get started by uploading your first dataset
      </p>
      <div className="mt-6">
        <button
          type="button"
          className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center text-sm transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200"
          onClick={() => {
            // Switch to file upload tab first, then open file dialog
            setActiveTab('files');
            // Use setTimeout to ensure the tab change is processed before opening the file dialog
            setTimeout(() => fileInputRef.current?.click(), 100);
          }}
        >
          <UploadIcon className="w-4 h-4 mr-2" />
          Upload Files
        </button>
      </div>
    </div>
  );

  // Render empty state for database connections

  // Get dataset info for an upload
  const getDatasetInfo = (upload) => {
    let datasetName = upload.dataset_name;
    
    // If no explicit dataset_name, check if this upload_id appears in any dataset
    if (!datasetName) {
      for (const [name, versions] of Object.entries(datasets)) {
        if (versions.some(v => v.upload_id === upload.upload_id)) {
          datasetName = name;
          break;
        }
      }
    }
    
    if (!datasetName) return null;
    
    const datasetVersions = datasets[datasetName] || [];
    const versionCount = datasetVersions.length;
    const isLatest = upload.is_latest;
    
    return {
      datasetName,
      versionCount,
      isLatest,
      hasVersions: versionCount > 1
    };
  };

  // Render the data sources list (original upload-based view with versioning info)
  const renderDataSources = () => (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg">
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-slate-300 dark:border-slate-600 border-t-slate-600 dark:border-t-slate-300 rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading...</p>
        </div>
      ) : uploads.length > 0 ? (
        <div className="space-y-0">
          {uploads.filter(upload => {
            if (!searchTerm) return true;
            return upload.upload_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   upload.files.some(file => file.filename.toLowerCase().includes(searchTerm.toLowerCase())) ||
                   upload.dataset_name?.toLowerCase().includes(searchTerm.toLowerCase());
          }).map((upload) => {
            const datasetInfo = getDatasetInfo(upload);
            
            return (
              <Link
                key={upload.upload_id}
                to={`/datasets/${upload.upload_id}`}
                className="relative flex items-center gap-4 p-4 hover:bg-slate-100 dark:hover:bg-slate-800/50 transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group border-b border-slate-200 dark:border-slate-700 last:border-b-0 cursor-pointer"
              >
                {/* File Icon */}
                <div className="flex-shrink-0">
                  <FolderIcon className="w-5 h-5 text-primary-500" />
                </div>

                {/* Name and Files */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-900 dark:text-white truncate">
                      {datasetInfo && datasetInfo.datasetName
                        ? datasetInfo.datasetName
                        : (upload.files && upload.files.length > 0
                            ? upload.files[0].filename
                            : upload.upload_id.substring(0, 8))}
                    </h3>

                    {/* Demo dataset badge */}
                    {upload.upload_id.includes('-demo-') && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                        Demo
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 truncate">
                    {upload.files && upload.files.length > 1 && (
                      <span>+{upload.files.length - 1} more files</span>
                    )}
                    {upload.version_description && (
                      <span className="truncate">{upload.files && upload.files.length > 1 ? '• ' : ''}{upload.version_description}</span>
                    )}
                  </div>
                </div>

                {/* Status badge - fixed width column */}
                <div className="w-24 flex justify-end flex-shrink-0">
                  {upload.status && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      upload.status === 'ready' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400' :
                      upload.status === 'processing' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400' :
                      upload.status === 'pending' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400' :
                      upload.status === 'failed' ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                      {upload.status.charAt(0).toUpperCase() + upload.status.slice(1)}
                    </span>
                  )}
                </div>

                {/* Type badge - fixed width column */}
                <div className="w-24 flex justify-end flex-shrink-0">
                  {upload.type && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      upload.type === 'unknown'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}>
                      {upload.type === 'tabular' ? 'Table' :
                       upload.type === 'images' ? 'Images' :
                       upload.type === 'text_corpus' ? 'Text' :
                       upload.type === 'time_series' ? 'Time Series' :
                       upload.type === 'unknown' ? 'Unknown' :
                       upload.type}
                    </span>
                  )}
                </div>

                {/* Created Date */}
                <div className="text-sm text-slate-500 dark:text-slate-400 w-28 text-right flex-shrink-0">
                  {upload.created_at ? new Date(upload.created_at).toLocaleDateString() : 'Unknown'}
                </div>

                {/* Size */}
                <div className="text-sm text-slate-600 dark:text-slate-300 w-20 text-right flex-shrink-0">
                  {upload.size
                    ? formatFileSize(upload.size)
                    : 'Unknown'}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 ml-6">
                  {/* Show versions button for any dataset */}
                  {datasetInfo && (
                    <button
                      type="button"
                      className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 rounded-lg transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleViewVersions(datasetInfo.datasetName);
                      }}
                    >
                      Versions ({datasetInfo.versionCount})
                    </button>
                  )}

                  {/* Download button */}
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 transform hover:scale-110 transition-all duration-200 p-1 text-slate-400 hover:text-primary-500"
                    onClick={(e) => handleDownloadDataset(upload.upload_id, e)}
                    title="Download dataset"
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </button>

                  <button
                    type="button"
                    className={`opacity-0 group-hover:opacity-100 transform hover:scale-110 transition-all duration-200 p-1 ${
                      showDeleteConfirm === upload.upload_id
                        ? 'text-red-500 hover:text-red-600'
                        : 'text-slate-400 hover:text-red-500'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (showDeleteConfirm === upload.upload_id) {
                        handleDeleteUpload(upload.upload_id);
                      } else {
                        setShowDeleteConfirm(upload.upload_id);
                        setTimeout(() => {
                          setShowDeleteConfirm(null);
                        }, 3000);
                      }
                    }}
                    title={showDeleteConfirm === upload.upload_id ? "Click again to confirm delete" : "Delete upload"}
                  >
                    {showDeleteConfirm === upload.upload_id ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <Trash2Icon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="p-8">
          {renderEmptyDataSources()}
        </div>
      )}
      
      {/* Dataset Versions Modal */}
      {selectedDatasetForVersions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedDatasetForVersions(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <DatabaseIcon className="w-6 h-6 text-primary-500 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedDatasetForVersions} Versions
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {datasetVersions.length} version{datasetVersions.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedDatasetForVersions(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Versions List */}
            <div className="overflow-y-auto max-h-[calc(80vh-120px)]">
              {datasetVersions.length > 0 ? (
                <div className="p-6">
                  <div className="space-y-3">
                    {datasetVersions.map((version, index) => (
                      <div
                        key={version.upload_id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600/50 transition-colors"
                      >
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 flex items-center justify-center">
                              {version.is_latest && (
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                              )}
                            </div>
                            <FolderIcon className="w-5 h-5 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-gray-900 dark:text-white">
                                {version.files && version.files[0] ? version.files[0].filename : 
                                 version.version_description || `Version ${index + 1}`}
                              </p>
                              {version.is_latest && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                  Latest
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {version.version_description && `${version.version_description} • `}
                              Created: {new Date(version.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            ID: {version.upload_id.substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No versions found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render tabs
  const renderTabs = () => (
    <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav className="flex -mb-px">
        <button
          onClick={() => setActiveTab('overview')}
          className={`mr-8 py-4 border-b-2 font-medium text-sm flex items-center transform hover:scale-[1.02] transition-all duration-200 ${
            activeTab === 'overview'
              ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <FolderIcon className="w-4 h-4 mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('files')}
          className={`mr-8 py-4 border-b-2 font-medium text-sm flex items-center transform hover:scale-[1.02] transition-all duration-200 ${
            activeTab === 'files'
              ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <UploadIcon className="w-4 h-4 mr-2" />
          File Upload
        </button>
        <button
          onClick={() => setActiveTab('connectors')}
          className={`mr-8 py-4 border-b-2 font-medium text-sm flex items-center transform hover:scale-[1.02] transition-all duration-200 ${
            activeTab === 'connectors'
              ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <ServerIcon className="w-4 h-4 mr-2" />
          Connectors
        </button>
        <button
          onClick={() => setActiveTab('custom-connectors')}
          className={`py-4 border-b-2 font-medium text-sm flex items-center transform hover:scale-[1.02] transition-all duration-200 ${
            activeTab === 'custom-connectors'
              ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <DatabaseIcon className="w-4 h-4 mr-2" />
          Custom Connectors
          <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
            New
          </span>
        </button>
      </nav>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hidden file input always available for Quick Upload */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept=".csv,.parquet"
      />
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Datasets</h1>
        <div className="flex items-center gap-3">
          {activeTab === 'files' && (
            <button
              type="button"
              className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload Files
            </button>
          )}
          {activeTab === 'overview' && (
            <button
              type="button"
              className="px-4 py-2 bg-primary-600 text-white rounded-md shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 inline-flex items-center transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] transition-all duration-200"
              onClick={() => {
                // Switch to file upload tab first, then open file dialog
                setActiveTab('files');
                // Use setTimeout to ensure the tab change is processed before opening the file dialog
                setTimeout(() => fileInputRef.current?.click(), 100);
              }}
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Quick Upload
            </button>
          )}
        </div>
      </div>
      
      {renderTabs()}

      {activeTab === 'overview' && (
        <>
          {/* Search bar with refresh button */}
          <div className="mb-6 flex gap-3 items-center">
            <div className="relative flex-1">
              <input
                type="text"
                className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 focus:border-transparent"
                placeholder="Search datasets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center transform hover:scale-110 transition-all duration-200"
                >
                  <XCircleIcon className="h-4 w-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" />
                </button>
              )}
            </div>
            <button
              type="button"
              className="p-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transform hover:scale-110 transition-all duration-200"
              onClick={() => fetchUploads()}
              disabled={loading}
              title="Refresh datasets"
            >
              <RefreshCwIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {renderDataSources()}
        </>
      )}
      
      {activeTab === 'files' && (
        <>
          {renderUploadArea()}
          {renderDataSources()}
        </>
      )}

      {activeTab === 'connectors' && (
        <ConnectorsTab />
      )}

      {activeTab === 'custom-connectors' && (
        <MintsoftConnectorsTab />
      )}
    </div>
  );
}

export default DataSources;